/**
 * Visualization utility values and functions.
 *
 * Pure constants and functions are defined here canonically.
 * Color maps, legend functions, and trade value computation are
 * all self-contained with no src/ dependencies.
 */

import { sum } from "d3-array";
import { format } from "d3-format";
import { TradeDirection, TradeFlow } from "../types/enums";
import { TradeDirectionSelectorString, UIView } from "../types/vizEnums";
import { worldGroupDatum } from "../graphql/queries/getLocationsMetadata";

// ── Canonical implementations (no src/ dependencies) ─────────────

/** Opacity applied to non-highlighted items in visualizations */
export const highlightInVizReducedOpacity = 0.4;

/** DOM element id for the overlay portal container */
export const overlayPortalContainerId = "overlayPortalContainerId";

/** Convert an rgb() CSS value to rgba() with the given alpha transparency */
export const rgba = (input: string, alpha: number) => {
  const re = /(\d+)/g;

  if (input.includes("rgb")) {
    const rgb = input.match(re);
    if (rgb && rgb.length >= 3) {
      return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
    }
  }

  const hex = input.replace("#", "");
  if (/^[0-9a-fA-F]{3,8}$/.test(hex)) {
    const full = hex.length === 3
      ? hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
      : hex;
    const r = parseInt(full.substring(0, 2), 16);
    const g = parseInt(full.substring(2, 4), 16);
    const b = parseInt(full.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  return input;
};

/** Format a monetary value with SI prefix (K/M/B) */
export const formatTotalValue = (value: number): string => {
  return format(".3s")(value).replace("G", "B");
};

/** Format a Y-axis value with SI prefix (K/M/B) */
export const formatYAxisValue = (value: number): string => {
  return format(".2s")(value).replace("G", "B");
};

/** Format a number as a percentage (e.g. 0.1234 -> "12.34") */
export const formatAsPercentage = (value: number): string => {
  return format(",.2f")(value * 100);
};

/** Compact number formatter for monetary display */
export const moneyFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  compactDisplay: "short",
});

// ── Trade value computation (pure functions, enum-only deps) ─────

/** Datum shape accepted by monetary value computation functions. */
export interface TradeValueDatum {
  exportValue?: number;
  importValue?: number;
  value?: number;
}

/**
 * Access a nested scaled trade value from a datum.
 *
 * Overtime transforms attach scaled values as nested objects keyed by
 * a scaling option string (e.g., `"current"`, `"constant"` from the
 * viz layer's `TradeValueScaledByYAxisOption`). This helper accesses
 * those nested values without requiring an unsafe `as Record<...>` cast
 * at every call site.
 */
function getScaledValue(
  datum: TradeValueDatum,
  scalingOption: string,
  field: "exportValue" | "importValue",
): number {
  const scaled = (datum as Record<string, unknown>)[scalingOption];
  if (scaled != null && typeof scaled === "object") {
    return +((scaled as Record<string, number>)[field] ?? 0);
  }
  return 0;
}

/** Compute the monetary value for a single datum based on trade flow and direction */
export const computeMonetaryValueByTradeFlow = ({
  datum,
  tradeFlow,
  tradeDirection,
  tradeDirectionSelector: _tradeDirectionSelector,
}: {
  datum: TradeValueDatum;
  tradeFlow?: TradeFlow;
  tradeDirection?: TradeDirection;
  tradeDirectionSelector?: TradeDirectionSelectorString;
}) => {
  // For Markets view, transformLocations pre-computes a 'value' property that correctly
  // accounts for World exporter/importer context. Use it if available.
  if (datum.value !== undefined && tradeFlow === TradeFlow.Gross) {
    return datum.value;
  }

  let monetaryValue: number | undefined;
  const exp = +(datum.exportValue ?? 0);
  const imp = +(datum.importValue ?? 0);

  if (tradeFlow === TradeFlow.Gross) {
    if (tradeDirection === TradeDirection.Exports) {
      monetaryValue = exp;
    } else if (tradeDirection === TradeDirection.Imports) {
      monetaryValue = imp;
    } else {
      monetaryValue = undefined;
    }
  } else if (tradeFlow === TradeFlow.Net) {
    if (tradeDirection === TradeDirection.Exports) {
      monetaryValue = exp - imp;
    } else if (tradeDirection === TradeDirection.Imports) {
      monetaryValue = imp - exp;
    } else {
      monetaryValue = undefined;
    }
    if (monetaryValue !== undefined && monetaryValue < 0) {
      monetaryValue = 0;
    }
  }

  return monetaryValue;
};

/** Compute the total sum across data based on trade flow and direction */
export const computeTotalSumByTradeFlow = ({
  data,
  tradeFlow,
  tradeDirection,
  scalingOption,
}: {
  data: TradeValueDatum[];
  tradeFlow: TradeFlow;
  tradeDirection: TradeDirection;
  scalingOption?: string;
}) => {
  const getExp = (d: TradeValueDatum) =>
    scalingOption
      ? getScaledValue(d, scalingOption, "exportValue")
      : +(d.exportValue ?? 0);
  const getImp = (d: TradeValueDatum) =>
    scalingOption
      ? getScaledValue(d, scalingOption, "importValue")
      : +(d.importValue ?? 0);

  if (tradeFlow === TradeFlow.Gross) {
    return tradeDirection === TradeDirection.Exports
      ? sum(data, getExp)
      : sum(data, getImp);
  } else if (tradeFlow === TradeFlow.Net) {
    const [getPrimary, getSecondary] =
      tradeDirection === TradeDirection.Exports
        ? [getExp, getImp]
        : [getImp, getExp];
    return sum(data, (d) => {
      const diff = getPrimary(d) - getSecondary(d);
      return diff > 0 ? diff : 0;
    });
  }
  return undefined;
};

// ── Trade direction utilities ─────────────────────────────────────

export const getTradeDirectionSelector = ({ tradeDirection }: { tradeDirection: TradeDirection }): TradeDirectionSelectorString => {
  if (tradeDirection === TradeDirection.Exports) {
    return TradeDirectionSelectorString.Exports;
  }
  return TradeDirectionSelectorString.Imports;
};

/**
 * Computes the effective trade direction for data selection.
 *
 * For World->World queries in Markets view, the API returns data with inverted semantics:
 * - Each country's `exportValue` represents what was exported TO that country (their imports)
 * - Each country's `importValue` represents what was exported FROM that country (their exports)
 *
 * This means for World->World Markets view queries, we need to swap:
 * - tradeDirection=exports (show who exported) -> read importValue
 * - tradeDirection=imports (show who imported) -> read exportValue
 *
 * For all other cases (Country->World, World->Country, bilateral), no swap is needed.
 */
export const getDataTradeDirection = ({
  tradeDirection,
  exporter,
  importer,
  view,
}: {
  tradeDirection: TradeDirection;
  exporter: string | undefined;
  importer: string | undefined;
  view: UIView | undefined;
}): TradeDirection => {
  const isWorldExporter = exporter === worldGroupDatum.groupId;
  const isWorldImporter = importer === worldGroupDatum.groupId;

  // Only swap for World->World queries in Markets view
  if (view === UIView.Markets && isWorldExporter && isWorldImporter) {
    return tradeDirection === TradeDirection.Exports
      ? TradeDirection.Imports
      : TradeDirection.Exports;
  }

  // All other cases: use tradeDirection directly
  return tradeDirection;
};

/**
 * Returns the trade direction selector string for data selection.
 * Convenience function combining getDataTradeDirection and getTradeDirectionSelector.
 */
export const getDataTradeDirectionSelector = ({
  tradeDirection,
  exporter,
  importer,
  view,
}: {
  tradeDirection: TradeDirection;
  exporter: string | undefined;
  importer: string | undefined;
  view: UIView | undefined;
}): TradeDirectionSelectorString => {
  const effectiveDirection = getDataTradeDirection({
    tradeDirection,
    exporter,
    importer,
    view,
  });
  return getTradeDirectionSelector({ tradeDirection: effectiveDirection });
};

/**
 * Returns the appropriate trade value based on trade direction and query context.
 *
 * Handles the World->World swap when necessary (via isWorldToWorldQuery flag).
 *
 * Note: For new code, prefer using getDataTradeDirection() to get the effective
 * direction, then select exportValue/importValue based on that.
 */
export const getTradeValueForWorldSelection = ({
  exportValue,
  importValue,
  tradeDirection,
  isWorldToWorldQuery = false,
}: {
  exportValue: number;
  importValue: number;
  tradeDirection: TradeDirection;
  isWorldToWorldQuery?: boolean;
}) => {
  if (isWorldToWorldQuery) {
    return tradeDirection === TradeDirection.Exports
      ? importValue
      : exportValue;
  }

  return tradeDirection === TradeDirection.Exports ? exportValue : importValue;
};

